import React, { useState, useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { all_routes } from "Router/all_routes";
import { Link } from "react-router-dom";
import Table from "core/pagination/datatable";
import TableTop from "Components/UI/TableTop";
import { useTableSearch } from "core/Operations/useTableSearch";
import { Edit, PlusCircle } from "feather-icons-react/build/IconComponents";
import Error404 from "MainPages/ErrorPages/error404";
import LeaveTypeForm from "./LeaveTypeForm";
import { useForm, FormProvider } from "react-hook-form";
import { loggedInUser } from "core/redux/slice/authSlice";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import ToastNotification from "Components/UI/ToastNotification";
import { yupResolver } from "@hookform/resolvers/yup";
import LoadingSpinner from "Components/UI/LoadingSpinner";
import { leaveTypeValidationSchema } from "core/Operations/ValidationSchema/MasterValidationSchema";
import {
  useAddLeaveMutation,
  useGetAllLeaveTypesQuery,
  useUpdateLeaveMutation,
} from "AppQuries/leave/leave-types";
import { toast } from "core/Operations/toast";
import ReportTools from "Components/UI/ReportTools";
import { Switch } from "antd";
import Can from "core/Operations/Can";
import { accessCodes } from "core/json/accessCode";

const LeaveType = () => {
  const userData = useSelector(loggedInUser);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [isMount, setIsMount] = useState(false);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const toggleFilterVisibility = () => {
    setIsFilterVisible((prevVisibility) => !prevVisibility);
  };

  const methods = useForm({
    defaultValues: {
      noOfLeaveDays: 0,
      isActive: true,
      leaveName: "",
      companyCode: userData.companyCode,
      createUser: userData.userName,
      modifyUser: userData.userName,
      createDate: `${format(new Date(), "yyyy-MM-dd")}T${format(
        new Date(),
        "HH:mm:ss.SSS"
      )}Z`,
      modifyDate: `${format(new Date(), "yyyy-MM-dd")}T${format(
        new Date(),
        "HH:mm:ss.SSS"
      )}Z`,
    },
    resolver: yupResolver(leaveTypeValidationSchema),
  });
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch: GetLeaves,
  } = useGetAllLeaveTypesQuery({
    companyCode: userData.companyCode,
    isTrue: showActiveOnly,
  });

  const { mutate: addLeave } = useAddLeaveMutation();
  const { mutate: updateLeave } = useUpdateLeaveMutation();

  const [selectedLeave, setSelectedLeave] = useState(null);

  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const leaveData = data?.data.map((d, _id) => ({
    id: _id + 1,
    ...d,
  }));

  const handleAddClick = () => {
    setSelectedLeave(null);
    methods.reset({
      noOfLeaveDays: 0,
      leaveCode: 0,
      leaveName: "",
      isActive: true,
      companyCode: userData.companyCode,
      createUser: userData.userName,
      modifyUser: userData.userName,
      createDate: `${format(new Date(), "yyyy-MM-dd")}T${format(
        new Date(),
        "HH:mm:ss.SSS"
      )}Z`,
      modifyDate: `${format(new Date(), "yyyy-MM-dd")}T${format(
        new Date(),
        "HH:mm:ss.SSS"
      )}Z`,
    });
    setShow(true);
  };

  const handleEditClick = (leave) => {
    setSelectedLeave(leave);
    methods.reset({
      leaveCode: leave.leaveCode,
      leaveName: leave.leaveName,
      noOfLeaveDays: leave.noOfLeaveDays,
      isActive: leave.isActive,
      companyCode: leave.companyCode,
      createUser: leave.createUser,
      modifyUser: userData.userName,
      createDate: leave.createDate,
      modifyDate: `${format(new Date(), "yyyy-MM-dd")}T${format(
        new Date(),
        "HH:mm:ss.SSS"
      )}Z`,
    });
    setShow(true);
  };

  const onSubmit = async (payload) => {
    console.log(payload);
    if (selectedLeave) {
      updateLeave(payload, {
        onSuccess: (data) => {
          if (data?.data) {
            toast.success("Leave Updated Successfully.");

            setSelectedLeave(null);
            setShow(false);
            GetLeaves();
          } else {
            toast.warning("Something went wrong.");
          }
        },
        onError: (error) => {
          toast.error(error.message || "Something went wrong.");
        },
      });
    } else {
      addLeave(payload, {
        onSuccess: (data) => {
          if (data?.data) {
            toast.success("Leave Added Successfully.");
            setShow(false);
            GetLeaves();
          } else {
            toast.warning(error.message || "Something went wrong.");
          }
        },
        onError: (error) => {
          toast.error(error.message || "Something went wrong.");
        },
      });
    }
  };

  const columns = [
    {
      title: "Leave Name",
      dataIndex: "leaveName",
      sorter: (a, b) => a.leaveName.length - b.leaveName.length,
    },

    {
      title: "Created User",
      dataIndex: "createUser",
      sorter: (a, b) => a.createUser.length - b.createUser.length,
    },

    {
      title: "Status",
      dataIndex: "isActive",
      render: (text, record) => (
        <>
          {text === true && (
            <div className="badges badge-linesuccess">Active</div>
          )}
          {text === false && (
            <div className="badges badge-linedanger">In Active</div>
          )}
        </>
      ),
    },

    {
      title: "Action",
      className: "action-table-data",
      render: (text, record) => (
        <div className="edit-delete-action">
          <div className="input-block add-lists"></div>
          <Can accessModule={accessCodes.leave_adjustment} accessCode={accessCodes.leave_categories} accessType="haveEdit">
          <Link
            className="me-2 p-2"
            to="#"
            onClick={() => handleEditClick(record)}
          >
            <Edit className="feather-edit" />
          </Link>
          </Can>
        </div>
      ),
    },
  ];

  const searchColumns = columns.map((column) => column.dataIndex);
  const { setSearchValue, filteredData } = useTableSearch(
    leaveData,
    searchColumns
  );

  useEffect(() => {
      if (isMount) GetLeaves();
  
      setIsMount(true);
    }, [showActiveOnly]);
  

  if (isLoading || isFetching) return <LoadingSpinner />;
  if (isError) return <Error404 error={error} />;

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Leaves</h4>
                <h6>Manage your Leaves</h6>
              </div>
            </div>
            <div className="page-btn">
              <ReportTools
                columns={columns}
                data={filteredData}
                fileName="Leave Master"
                isExcel={true}
                isPdf={true}
                pdfTheme="striped"
                refresh={GetLeaves}
              />
            </div>
            <div className="page-btn">
              <Can accessModule={accessCodes.leave_adjustment} accessCode={accessCodes.leave_categories} accessType="haveAdd">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="add-new-tooltip">Add New Leave</Tooltip>}
              >
                <Link to="#" className="btn btn-added" onClick={handleAddClick}>
                  <PlusCircle className="me-2" />
                  Add New Leave
                </Link>
              </OverlayTrigger>
              </Can>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="card table-list-card border-0">
                <div className="card-body">
                  <TableTop
                    onSearch={setSearchValue}
                    toggleFilterVisibility={toggleFilterVisibility}
                    isFilterVisible={isFilterVisible}
                    showToggle={true}
                  />
                  <div
                    className={`card${isFilterVisible ? " visible" : ""}`}
                    id="filter_inputs"
                    // style={{ display: isFilterVisible ? "block" : "none" }}
                  >
                    
                    <div className="card-body pb-0">
                      <div className="form-row row">
                        <div className="row">
                          <div className="col-lg-3 col-sm-6 col-12 mb-3">
                            <div className="form-group">
                              <Switch
                                checked={showActiveOnly}
                                checkedChildren="Show Active"
                                unCheckedChildren="Show All"
                                onChange={(checked) =>
                                  setShowActiveOnly(checked)
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <Table
                      className="table datanew"
                      columns={columns}
                      dataSource={filteredData}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FormProvider {...methods}>
        <LeaveTypeForm
          mode={selectedLeave ? "E" : "I"}
          onSubmit={onSubmit}
          show={show}
          handleClose={handleClose}
        />
      </FormProvider>
    </div>
  );
};

export default LeaveType;
